import {
    Component,
    model,
    signal,
    computed,
    ChangeDetectorRef,
    input,
    effect,
    ElementRef,
    viewChildren,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowActionsComponent } from "./actions/workflow.actions.component";
import { ExpandTrigger, WFError, WorkflowSteps } from "./workflow.process.types";
import { S25Util } from "../../util/s25-util";
import { WorkflowService } from "./service/workflow.service";
import { DataIngestionService } from "../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.const";
import {
    buildFormActionGroups,
    FormActionGroup,
    FORMLESS_FORM_ID,
    getActionStableId,
    getAdjacentSameStageActionInFormGroup,
    swapActionSortOrderInStage,
    findStageForAction,
} from "./workflow.action.grouping";

@TypeManagerDecorator("workflow-actions-step")
@Component({
    selector: "workflow-actions-step",
    template: `
        <div class="step-header">
            <h3>Actions</h3>
            <p class="step-description">
                Configure actions for each stage. Actions are executed when the stage completes.
            </p>
        </div>

        <div class="add-action-section">
            <div class="add-controls">
                <select [(ngModel)]="selectedStageForNewAction" class="stage-dropdown">
                    <option value="">Select stage...</option>
                    @for (stage of availableStagesForActions(); track stage.workflowStageId || stage.id) {
                        <option [value]="stage.workflowStageId || stage.id">
                            {{ stage.stageName }}
                        </option>
                    }
                </select>
                <button
                    class="aw-button aw-button--outline"
                    [disabled]="!selectedStageForNewAction"
                    (click)="addHttpActionToSelectedStage()"
                >
                    Add HTTP Action
                </button>
                <select
                    class="stage-dropdown"
                    [disabled]="!selectedStageForNewAction"
                    (change)="addSystemActionToSelectedStage($any($event.target).value); $any($event.target).value = ''"
                >
                    <option value="">Add System Action...</option>
                    @for (sa of systemActionOptions; track sa.actionCode) {
                        <option [value]="sa.actionCode">{{ sa.displayName }}</option>
                    }
                </select>
            </div>
            @if (hasSpawnWorkflowOnInvalidStage()) {
                <div class="validation-error">
                    ⚠️ Warning: Stages that spawn forms cannot be terminal or start stages. Please remove spawn form
                    actions from terminal/start stages or change the stage type.
                </div>
            }
        </div>

        @if (error()?.message) {
            <p class="ngRed c-margin-bottom--half" [innerHTML]="error()!.message"></p>
        }

        @if (referencedForms().length) {
            <div class="form-summary">
                <span class="form-summary-label">Referenced forms:&nbsp;</span>
                @for (form of referencedForms(); track form.id) {
                    <span class="form-link" [class.form-link--missing-context]="!hasActionWithFormContext(form.id)">{{
                        form.name
                    }}</span>
                }
            </div>
        }

        @if (formActionGroups().length) {
            <div class="form-groups-section">
                <p class="form-groups-description">
                    Actions are organized by form context. Each section below is a form; expand it to view and configure
                    the actions tied to that form. Actions without form context appear under
                    <strong class="formless-inline-label">Formless</strong>—assign a form on each of those actions when
                    possible.
                </p>

                <div class="form-groups-list">
                    @for (formGroup of formActionGroups(); track formGroupTrack(formGroup)) {
                        @let formGroupExpanded = isFormGroupExpanded(formGroup);
                        <div
                            class="form-group-card"
                            [class.form-group-highlight]="isFormGroupHighlighted(formGroup)"
                            [attr.data-form-group-id]="formGroup.formId ?? 'formless'"
                        >
                            <div
                                class="form-group-header"
                                [class.expanded]="formGroupExpanded"
                                (click)="toggleFormGroupExpansion(formGroup)"
                            >
                                <span
                                    class="form-group-name"
                                    [class.form-group-name--formless]="formGroup.formId == null"
                                    >{{ formGroup.formName }}</span
                                >
                                <span class="form-group-count">{{ formGroup.actions.length }} action(s)</span>
                                <span class="expand-icon" [class.expanded]="formGroupExpanded">
                                    {{ formGroupExpanded ? "▼" : "▶" }}
                                </span>
                            </div>

                            @if (formGroupExpanded) {
                                <div class="form-group-actions">
                                    @for (action of formGroup.actions; track getActionStableId(action)) {
                                        @let isExpanded = isActionExpanded(action);
                                        @let stage = findStageForAction(workflowProcess(), action);
                                        <div
                                            class="action-item"
                                            [class.action-highlight]="isActionHighlighted(action)"
                                            [attr.data-action-id]="getActionStableId(action)"
                                        >
                                            <div
                                                #actionHeader
                                                class="action-header"
                                                [class.expanded]="isExpanded"
                                                (click)="toggleActionExpansion(action)"
                                            >
                                                <div class="action-info">
                                                    <span class="action-name">{{ getActionDisplayName(action) }}</span>
                                                    <span class="action-type">{{ getActionTypeDisplay(action) }}</span>
                                                    <span class="stage-context">Stage: {{ stage?.stageName }}</span>
                                                    <span class="action-flags">
                                                        @if (action.defaultEnabled !== false) {
                                                            <span class="flag enabled">Enabled</span>
                                                        }
                                                    </span>
                                                </div>
                                                <div class="action-actions">
                                                    <div class="move-buttons">
                                                        <button
                                                            class="move-btn"
                                                            [disabled]="
                                                                !canMoveActionInFormGroup(formGroup, action, 'up')
                                                            "
                                                            (click)="
                                                                moveActionInFormGroup(formGroup, action, 'up');
                                                                $event.stopPropagation()
                                                            "
                                                            title="Move up within stage"
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            class="move-btn"
                                                            [disabled]="
                                                                !canMoveActionInFormGroup(formGroup, action, 'down')
                                                            "
                                                            (click)="
                                                                moveActionInFormGroup(formGroup, action, 'down');
                                                                $event.stopPropagation()
                                                            "
                                                            title="Move down within stage"
                                                        >
                                                            ↓
                                                        </button>
                                                    </div>
                                                    @if (availableStagesForActions().length > 1) {
                                                        <div class="copy-action-wrapper">
                                                            <button
                                                                class="copy-btn"
                                                                (click)="showCopyMenu(action); $event.stopPropagation()"
                                                                title="Copy to stage..."
                                                            >
                                                                Copy to...
                                                            </button>
                                                            @if (copyMenuAction() === action) {
                                                                <div
                                                                    class="copy-menu"
                                                                    (click)="$event.stopPropagation()"
                                                                >
                                                                    @for (
                                                                        targetStage of getCopyTargetStages(action);
                                                                        track targetStage.workflowStageId ||
                                                                            targetStage.id
                                                                    ) {
                                                                        <button
                                                                            class="copy-menu-item"
                                                                            (click)="
                                                                                copyActionToStage(
                                                                                    action,
                                                                                    (targetStage.workflowStageId ||
                                                                                        targetStage.id)!
                                                                                )
                                                                            "
                                                                        >
                                                                            {{ targetStage.stageName }}
                                                                        </button>
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                    }
                                                    <span class="expand-icon" [class.expanded]="isExpanded">
                                                        {{ isExpanded ? "▼" : "▶" }}
                                                    </span>
                                                </div>
                                            </div>

                                            @if (isExpanded) {
                                                <div class="action-content">
                                                    @if (
                                                        action.actionCode === "SPAWN_WORKFLOW" &&
                                                        (stage?.isTerminal || stage?.isStartStage)
                                                    ) {
                                                        <div class="validation-error">
                                                            ⚠️ This spawn form action is on a
                                                            {{ stage?.isTerminal ? "terminal" : "start" }} stage. Stages
                                                            that spawn forms cannot be terminal or start. Please remove
                                                            this action or change the stage type.
                                                        </div>
                                                    }
                                                    <div class="single-action-config">
                                                        <workflow-actions
                                                            [workflowProcess]="workflowProcess()"
                                                            [stageId]="stage?.workflowStageId || stage?.id"
                                                            [stage]="stage!"
                                                            [actions]="[action]"
                                                            (actionsChange)="onActionChange(action, $event)"
                                                            [hideAddButtons]="true"
                                                            [hideStageHeader]="true"
                                                            [autoMapOnFormContextChange]="true"
                                                            (formContextNavigated)="onFormContextNavigated($event)"
                                                        ></workflow-actions>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        }

        @if (formActionGroups().length === 0) {
            <div class="no-actions">
                <p>
                    No actions defined. Select a stage above and click "Add HTTP Action" or "Add System Action" to
                    create your first action.
                </p>
            </div>
        }
    `,
    styles: `
        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .step-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .add-action-section {
            margin-bottom: 24px;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .add-controls {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .stage-dropdown {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
        }

        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .action-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: visible;
            position: relative;
        }

        .action-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
            border-radius: 8px;
        }

        .action-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .action-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .action-type {
            font-size: 11px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg-hover);
            padding: 2px 6px;
            border-radius: 8px;
            text-transform: uppercase;
        }

        .stage-context {
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .action-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: var(--color--registration);
            color: white;
        }

        .action-actions {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .expand-icon {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .action-content {
            padding: 20px;
            border-top: 1px solid var(--color--border);
            overflow: visible;
            position: relative;
        }

        .no-actions {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }

        .validation-error {
            margin-top: 12px;
            padding: 12px;
            background: var(--color--warning);
            border: 1px solid var(--color--warning);
            border-radius: 4px;
            color: var(--color--surface-bg);
            font-size: 14px;
        }

        .error-index {
            border-color: var(--color--danger);
        }

        .form-summary {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            align-items: center;
            padding: 12px 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            margin-bottom: 16px;
            font-size: 13px;
        }

        .form-summary-label {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .form-link {
            padding: 2px 8px;
            background: var(--color--surface-2-bg);
            border-radius: 4px;
            color: var(--color--text);
            font-size: 0.875rem;
        }

        .form-link--missing-context {
            color: var(--color--danger);
            font-weight: bold;
        }

        .form-context-link {
            font-size: 12px;
            color: var(--color--text-muted);
        }

        .copy-action-wrapper {
            position: relative;
        }

        .copy-btn {
            padding: 4px 10px;
            font-size: 12px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            cursor: pointer;
            color: var(--color--text);
            white-space: nowrap;
        }

        .copy-btn:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .copy-menu {
            position: absolute;
            top: 100%;
            right: 0;
            z-index: 50;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            min-width: 180px;
            max-height: 240px;
            overflow-y: auto;
            margin-top: 4px;
        }

        .copy-menu-item {
            display: block;
            width: 100%;
            padding: 8px 14px;
            text-align: left;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 13px;
            color: var(--color--text);
        }

        .copy-menu-item:hover {
            background: var(--color--surface-2-bg);
        }

        .form-groups-section {
            margin-bottom: 16px;
        }

        .form-groups-description {
            margin: 0 0 16px 0;
            color: var(--color--text-muted);
            font-size: 14px;
            line-height: 1.5;
        }

        .formless-inline-label {
            color: var(--color--danger);
            font-weight: 700;
        }

        .form-groups-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .form-group-card {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: visible;
        }

        .form-group-card.form-group-highlight {
            box-shadow: 0 0 0 2px var(--color--link-text);
        }

        .form-group-header {
            padding: 14px 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 12px;
            border-radius: 8px;
        }

        .form-group-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .form-group-name {
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .form-group-name--formless {
            color: var(--color--danger);
            font-weight: 700;
        }

        .form-group-count {
            font-size: 12px;
            color: var(--color--text-muted);
            margin-left: auto;
        }

        .form-group-actions {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding: 12px;
            border-top: 1px solid var(--color--border);
        }

        .action-item.action-highlight {
            box-shadow: 0 0 0 2px var(--color--warning);
        }

        .move-buttons {
            display: flex;
            gap: 4px;
        }

        .move-btn {
            padding: 4px 8px;
            font-size: 12px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            cursor: pointer;
        }

        .move-btn:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, WorkflowActionsComponent],
})
export class WorkflowActionsStepComponent {
    workflowProcess = model.required<WorkflowTypes.WorkflowProcess>();
    error = input<WFError>();
    selectedActionTrigger = model<ExpandTrigger>();

    actionHeaders = viewChildren<ElementRef>("actionHeader");

    readonly findStageForAction = findStageForAction;
    readonly getActionStableId = getActionStableId;

    selectedStageForNewAction: string = "";
    private expandedActions = signal<Set<number | string>>(new Set());
    private expandedFormGroups = signal<Set<string>>(new Set(["formless"]));
    private highlightedFormGroupKey = signal<string | null>(null);
    private highlightedActionId = signal<number | null>(null);
    copyMenuAction = signal<WorkflowTypes.WorkflowAction | null>(null);

    readonly systemActionOptions = Object.values(WorkflowService.SYSTEM_ACTIONS).sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
    );

    private formNameMap = signal<Map<number, string>>(new Map());

    readonly referencedForms = computed(() => {
        const map = this.formNameMap();
        const ids = this.referencedFormIds();
        return ids.map((id) => ({ id, name: map.get(id) || `Form ${id}` })).filter((form) => map.has(form.id));
    });

    readonly formActionGroups = computed(() => buildFormActionGroups(this.workflowProcess(), this.formNameMap()));

    protected errorAtIndex = (index: number) => this.error()?.indices?.some((i) => i === index);

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            // expand affected index when error triggered
            if (this.error()?.indices?.length && this.actionHeaders().length) {
                const firstIdx = this.error()!.indices[0];
                const errorHeader = this.actionHeaders()[firstIdx]?.nativeElement;
                if (!errorHeader?.classList.contains("expanded")) errorHeader.click();
            }
        });

        effect(() => {
            const expandTrigger = this.selectedActionTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.Actions) {
                if (expandTrigger.formId !== undefined) {
                    this.expandFormGroupById(expandTrigger.formId);
                }
                if (S25Util.isDefined(expandTrigger.id)) {
                    this.expandActionById(expandTrigger.id);
                }
                this.scrollToFormAndAction(expandTrigger.formId, expandTrigger.id);
                this.selectedActionTrigger.set(undefined);
            }
        });

        effect(() => {
            const ids = this.referencedFormIds();
            if (ids.length) this.loadFormNames(ids);
        });
    }

    private commitWorkflowProcessChange(): void {
        this.workflowProcess.set({ ...this.workflowProcess() });
    }

    getAllActions(): WorkflowTypes.WorkflowAction[] {
        const allActions: WorkflowTypes.WorkflowAction[] = [];
        this.workflowProcess().stages?.forEach((stage) => {
            if (stage.actions && stage.actions.length > 0) {
                allActions.push(...stage.actions);
            }
        });
        return allActions;
    }

    getStageForAction(action: WorkflowTypes.WorkflowAction): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.actions?.some(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === action.workflowActionId) ||
                        (a.id && a.id === action.id),
                ),
            ) || null
        );
    }

    getActionDisplayName(action: WorkflowTypes.WorkflowAction): string {
        if (action.description && action.description.trim()) {
            return action.description;
        }

        if (action.actionCode) {
            const systemDisplayName = WorkflowService.getSystemActionDisplayName(action.actionCode);
            if (systemDisplayName) {
                return systemDisplayName;
            }
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }

        return `${action.actionType} Action`;
    }

    getActionTypeDisplay(action: WorkflowTypes.WorkflowAction): string {
        if (action.actionType === "SYSTEM_ACTION" && action.actionCode) {
            const systemDisplayName = WorkflowService.getSystemActionDisplayName(action.actionCode);
            if (systemDisplayName) {
                return systemDisplayName;
            }
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }
        return action.actionType;
    }

    addHttpActionToSelectedStage() {
        if (!this.selectedStageForNewAction) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewAction) ||
                (s.id && s.id.toString() === this.selectedStageForNewAction),
        );

        if (stage) {
            // Accumulator stages cannot have actions - add a stage after the accumulator instead
            if (stage.isAccumulator || stage.isApAccumulator) {
                return;
            }

            const newAction: WorkflowTypes.WorkflowAction = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                workflowStageId: stage.workflowStageId || stage.id,
                actionType: "HTTP_REQUEST",
                isSystemAction: false,
                description: "HTTP Request Action",
                defaultEnabled: true,
                sortOrder: (stage.actions || []).length + 1,
            };

            if (!stage.actions) {
                stage.actions = [];
            }
            stage.actions.push(newAction);

            this.commitWorkflowProcessChange();
            this.cd.detectChanges();
            this.expandFormGroupById(FORMLESS_FORM_ID);
            this.expandActionById(newAction.id!);
            this.flashNavigationHighlight(FORMLESS_FORM_ID, newAction.id!);
            this.scrollToFormAndAction(FORMLESS_FORM_ID, newAction.id!);
        }
    }

    addSystemActionToSelectedStage(actionCode: string) {
        if (!this.selectedStageForNewAction || !actionCode) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewAction) ||
                (s.id && s.id.toString() === this.selectedStageForNewAction),
        );

        if (stage) {
            // Accumulator stages cannot have actions - add a stage after the accumulator instead
            if (stage.isAccumulator || stage.isApAccumulator) return;

            const newAction: WorkflowTypes.WorkflowAction = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                workflowStageId: stage.workflowStageId || stage.id,
                actionType: "SYSTEM_ACTION",
                actionCode: actionCode as WorkflowTypes.WorkflowAction["actionCode"],
                isSystemAction: true,
                description: "",
                defaultEnabled: true,
                sortOrder: (stage.actions || []).length + 1,
                inputs: [],
            };

            if (!stage.actions) {
                stage.actions = [];
            }
            stage.actions.push(newAction);

            this.commitWorkflowProcessChange();
            this.cd.detectChanges();
            this.expandFormGroupById(FORMLESS_FORM_ID);
            this.expandActionById(newAction.id!);
            this.flashNavigationHighlight(FORMLESS_FORM_ID, newAction.id!);
            this.scrollToFormAndAction(FORMLESS_FORM_ID, newAction.id!);
        }
    }

    formGroupTrack(formGroup: FormActionGroup): string {
        return formGroup.formId == null ? "formless" : String(formGroup.formId);
    }

    formGroupKey(formGroup: FormActionGroup): string {
        return formGroup.formId == null ? "formless" : String(formGroup.formId);
    }

    isFormGroupExpanded(formGroup: FormActionGroup): boolean {
        return this.expandedFormGroups().has(this.formGroupKey(formGroup));
    }

    toggleFormGroupExpansion(formGroup: FormActionGroup): void {
        const key = this.formGroupKey(formGroup);
        const expanded = this.expandedFormGroups();
        if (expanded.has(key)) {
            expanded.delete(key);
        } else {
            expanded.add(key);
        }
        this.expandedFormGroups.set(new Set(expanded));
    }

    expandFormGroupById(formId: number | null | undefined): void {
        const key = formId == null ? "formless" : String(formId);
        const expanded = this.expandedFormGroups();
        if (!expanded.has(key)) {
            expanded.add(key);
            this.expandedFormGroups.set(new Set(expanded));
        }
    }

    isFormGroupHighlighted(formGroup: FormActionGroup): boolean {
        return this.highlightedFormGroupKey() === this.formGroupKey(formGroup);
    }

    isActionHighlighted(action: WorkflowTypes.WorkflowAction): boolean {
        return this.highlightedActionId() === getActionStableId(action);
    }

    onFormContextNavigated(nav: { actionId: number; formId: number | null }): void {
        this.commitWorkflowProcessChange();
        this.cd.detectChanges();
        this.expandFormGroupById(nav.formId);
        this.expandActionById(nav.actionId, false);
        this.flashNavigationHighlight(nav.formId, nav.actionId);
        requestAnimationFrame(() => {
            this.cd.detectChanges();
            this.scrollToFormAndAction(nav.formId, nav.actionId);
        });
    }

    canMoveActionInFormGroup(
        formGroup: FormActionGroup,
        action: WorkflowTypes.WorkflowAction,
        direction: "up" | "down",
    ): boolean {
        return !!getAdjacentSameStageActionInFormGroup(formGroup, this.workflowProcess(), action, direction);
    }

    moveActionInFormGroup(
        formGroup: FormActionGroup,
        action: WorkflowTypes.WorkflowAction,
        direction: "up" | "down",
    ): void {
        const neighbor = getAdjacentSameStageActionInFormGroup(formGroup, this.workflowProcess(), action, direction);
        if (!neighbor) return;
        swapActionSortOrderInStage(this.workflowProcess(), action, neighbor);
        this.commitWorkflowProcessChange();
        this.cd.detectChanges();
    }

    private flashNavigationHighlight(formId: number | null, actionId: number): void {
        this.highlightedFormGroupKey.set(formId == null ? "formless" : String(formId));
        this.highlightedActionId.set(actionId);
        setTimeout(() => {
            this.highlightedFormGroupKey.set(null);
            this.highlightedActionId.set(null);
        }, 2000);
    }

    private scrollToFormAndAction(formId: number | null | undefined, actionId?: number): void {
        setTimeout(() => {
            const formKey = formId == null ? "formless" : String(formId);
            const formEl = this.el.nativeElement.querySelector(`[data-form-group-id="${formKey}"]`);
            formEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            if (S25Util.isDefined(actionId)) {
                const actionEl = this.el.nativeElement.querySelector(`[data-action-id="${actionId}"]`);
                actionEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
        }, 0);
    }

    private getNextNegativeId(): number {
        const allActions = this.getAllActions();
        if (allActions.length === 0) {
            return -1;
        }
        return allActions.reduce((minId, action) => Math.min(minId, action.id || -1), -1) - 1;
    }

    toggleActionExpansion(action: WorkflowTypes.WorkflowAction) {
        const actionKey = action.workflowActionId || action.id!;
        const expanded = this.expandedActions();

        if (expanded.has(actionKey)) {
            expanded.delete(actionKey);
        } else {
            expanded.add(actionKey);
        }

        this.expandedActions.set(new Set(expanded));
    }

    isActionExpanded(action: WorkflowTypes.WorkflowAction): boolean {
        const actionKey = action.workflowActionId || action.id!;
        return this.expandedActions().has(actionKey);
    }

    expandActionById(actionId: number | string, scrollToAction = true) {
        const expanded = this.expandedActions();
        if (!expanded.has(actionId)) {
            expanded.add(actionId);
            this.expandedActions.set(new Set(expanded));
        }
        if (!scrollToAction) return;
        setTimeout(() => {
            const actionEl = this.el.nativeElement.querySelector(`[data-action-id="${actionId}"]`);
            actionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }

    onActionChange(originalAction: WorkflowTypes.WorkflowAction, updatedActions: WorkflowTypes.WorkflowAction[]) {
        const stage = this.getStageForAction(originalAction);
        if (stage && stage.actions) {
            if (updatedActions.length === 0) {
                // Action was removed, remove it from the stage
                const actionIndex = stage.actions.findIndex(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === originalAction.workflowActionId) ||
                        (a.id && a.id === originalAction.id),
                );
                if (actionIndex !== -1) {
                    stage.actions.splice(actionIndex, 1);
                }
            } else {
                // Action was updated, replace it
                const updatedAction = updatedActions[0];
                const actionIndex = stage.actions.findIndex(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === originalAction.workflowActionId) ||
                        (a.id && a.id === originalAction.id),
                );
                if (actionIndex !== -1) {
                    stage.actions[actionIndex] = updatedAction;
                }
            }
        }
        this.commitWorkflowProcessChange();
        this.cd.detectChanges();
    }

    onStageActionsChange(stage: WorkflowTypes.WorkflowStage, actions: WorkflowTypes.WorkflowAction[]) {
        stage.actions = actions;
        this.commitWorkflowProcessChange();
        this.cd.detectChanges();
    }

    /**
     * Get stages that are available for adding actions (accumulator stages are excluded)
     */
    readonly availableStagesForActions = computed(() =>
        (this.workflowProcess().stages || []).filter((stage) => !stage.isAccumulator && !stage.isApAccumulator),
    );

    /**
     * Check if there are spawn workflow actions on invalid stages (terminal or start)
     */
    hasSpawnWorkflowOnInvalidStage(): boolean {
        const stages = this.workflowProcess().stages || [];
        return stages.some((stage) => {
            if ((stage.isTerminal || stage.isStartStage) && stage.actions) {
                return stage.actions.some((action) => action.actionCode === "SPAWN_WORKFLOW");
            }
            return false;
        });
    }

    private readonly referencedFormIds = computed(() => {
        const ids = new Set<number>();
        for (const action of this.getAllActions()) {
            if (action.actionItemId && action.actionItemTypeId === INGESTION_POINT_TYPE_ID) {
                ids.add(action.actionItemId);
            }
            if (action.spawnChildParentItemId) {
                ids.add(action.spawnChildParentItemId);
            }
            action.dispatchMappings?.forEach((dispatchMapping) => {
                if (dispatchMapping.targetIngestionPointId) ids.add(dispatchMapping.targetIngestionPointId);
            });
        }
        return [...ids];
    });

    hasActionWithFormContext(formId: number): boolean {
        for (const stage of this.workflowProcess().stages ?? []) {
            for (const action of stage.actions ?? []) {
                if (
                    Number(action.actionItemId) === formId &&
                    Number(action.actionItemTypeId) === INGESTION_POINT_TYPE_ID
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    private async loadFormNames(ids: number[]) {
        const currentMap = this.formNameMap();
        const missing = ids.filter((id) => !currentMap.has(id));
        if (!missing.length) return;

        try {
            const fetched = await DataIngestionService.getIngestionPointNames(missing);
            const newMap = new Map(currentMap);
            for (const [id, name] of fetched) {
                newMap.set(id, name);
            }
            this.formNameMap.set(newMap);
        } catch {
            // skip
        }
    }

    getFormName(action: WorkflowTypes.WorkflowAction): string | null {
        if (!action.actionItemId || action.actionItemTypeId !== INGESTION_POINT_TYPE_ID) return null;
        return this.formNameMap().get(action.actionItemId) || null;
    }

    showCopyMenu(action: WorkflowTypes.WorkflowAction) {
        this.copyMenuAction.set(this.copyMenuAction() === action ? null : action);
    }

    // all stages except accumulator stages and the stage the action is in
    getCopyTargetStages(action: WorkflowTypes.WorkflowAction): WorkflowTypes.WorkflowStage[] {
        const currentStage = this.getStageForAction(action);
        const currentStageKey = currentStage?.workflowStageId || currentStage?.id;
        return (this.workflowProcess().stages || []).filter((s) => {
            if (s.isAccumulator || s.isApAccumulator) return false;
            const stageKey = s.workflowStageId || s.id;
            return stageKey !== currentStageKey;
        });
    }

    copyActionToStage(action: WorkflowTypes.WorkflowAction, targetStageId: number | string) {
        const targetStage = this.workflowProcess().stages?.find(
            (s) => (s.workflowStageId || s.id)?.toString() === targetStageId.toString(),
        );
        if (!targetStage) return;

        const clone = structuredClone(action);
        clone.workflowActionId = undefined;
        clone.id = this.getNextNegativeId();
        clone.workflowStageId = targetStage.workflowStageId || targetStage.id;
        clone.sortOrder = (targetStage.actions?.length || 0) + 1;
        clone.inputs?.forEach((inp) => {
            inp.workflowActionInputId = undefined;
            inp.id = this.getNextNegativeId();
        });
        clone.dispatchMappings?.forEach((dm) => {
            dm.workflowActionId = undefined;
        });

        if (!targetStage.actions) targetStage.actions = [];
        targetStage.actions.push(clone);

        this.copyMenuAction.set(null);
        this.commitWorkflowProcessChange();
        this.cd.detectChanges();
    }
}
